import { type IBaseEntity } from "types/apiResponse/apiResponse";
import { type BilledStatus } from "types/enum";

import { type ApprovalStatus } from "commonComponents/approvals/ApprovalViewer/ApprovalViewer.types";
import { type LinkedScheduleItemStatus } from "commonComponents/entity/linkedScheduleItem/LinkedScheduleItemStatusTag";
import { type IEntityLinkResponse } from "commonComponents/utilities/Grid/GridContainer.types";

import { type BillDocumentType } from "entity/bill/Bill/Bill.api.types";
import { type BillStatusType } from "entity/bill/common/BillStatus/BillStatus";
import { type LienWaiverStatusTypes } from "entity/lienWaiver/LienWaiver/LienWaiver.api.types";

/*
 * Per-cell value shapes for the Bills AG Grid, plus the typed row (`IBillGridRow`). These are the
 * RAW server shapes — client-data mode uses `transformEntityAgGrid`, which does NOT run the Kendo
 * data mappers, so each cell renderer maps its own value. Co-locating the cell shapes here (rather
 * than in the renderers) lets `IBillGridRow` reference them without a circular import.
 */

export interface IBillLinkResponse {
    builderId: number;
    jobId: number;
    id: number;
    title: string;
    isDuplicate: boolean;
}

export interface IBillAmountCellValue {
    value?: number | null;
    scale: number;
    isPartiallyPaid: boolean;
}

/**
 * Server currency shape (`{ value, scale }`) used by the `currency` columns (Amount paid,
 * Remaining balance) and by the amount column's footer total (`footerData[jsonKey]`).
 * transformEntityAgGrid does not flatten it, so renderers read `.value` themselves.
 */
export interface IBillAmountFooterValue {
    value?: number | null;
    scale?: number;
}

export interface IBillPayToResponse {
    payTo: string;
}

export interface IBillStatusResponse {
    paymentStatus?: BillStatusType;
    isSubRequested: boolean;
    documentType?: BillDocumentType;
}

export interface ILienWaiverStatusResponse {
    lienWaiverId: number;
    billId: number;
    jobId: number;
    status: LienWaiverStatusTypes | null;
    title: string;
}

/**
 * Kendo parity: the server marks Related POs/Related invoices `isExpandable: true`
 * (`BillList.api.json`), which the legacy grid's `BTGridCell` wraps in the generic
 * `BTGridExpandableCell`/`ReadMorePresentational` — a CSS clamp to 2 lines with a "Read
 * more"/"Read less" toggle for the rest. Each stacked tag in these AG Grid columns already renders
 * as exactly one line (never wrapped), so capping at this many *items* reproduces the same
 * 2-line-clamp result pixel-for-pixel, without `ReadMorePresentational`'s `ResizeObserver`-based
 * overflow measurement — which would reintroduce the render-then-measure reflow `getRowHeight`
 * (BillListAgGrid.tsx) exists to avoid (ado-282662). Cost codes/Variance codes instead render as a
 * single ellipsis-truncated line (`SelectCell` in billAgCellRenderers.tsx) — they don't use this cap.
 */
export const READ_MORE_ITEM_CAP = 2;

/** The stacked-tag columns that get Kendo's "read more" cap + toggle treatment. */
export const READ_MORE_JSON_KEYS = new Set(["relatedPurchaseOrders", "relatedOwnerInvoices"]);

/**
 * `cellRendererParams` shape for the "read more" columns. Baked per-column (each column's
 * `jsonKey` is closed over when `BillListAgGrid` builds `columnDefs`), so the renderer only needs
 * its own row id to read/toggle this cell's expand state. Threaded via `cellRendererParams`
 * (rather than `context`) because a `cellRendererParams` identity change is one AG Grid already
 * diffs per column and re-renders for — `context` changes alone do not refresh mounted cells.
 * Expand state itself still lives in `BillListAgGrid` (the grid-owning component), since
 * `getRowHeight` also needs it to size a row for expanded content.
 */
export interface IReadMoreCellRendererParams {
    isCellExpanded: (rowId: number) => boolean;
    onToggleCellExpanded: (rowId: number) => void;
}

/** Keys the expand-state Set held in `BillListAgGrid`, mirroring Kendo's per-row-per-column `IExpandMap`. */
export const getReadMoreExpandKey = (rowId: number, jsonKey: string): string =>
    `${rowId}:${jsonKey}`;

/** Raw server shape for the `dueDate` column (renderer maps `date` to a moment). */
export interface IDueDateResponse {
    date: string | null;
    linkedScheduleInfo: string | null;
    isPastDue: boolean;
    displayTime: boolean;
}

export interface IApproverInfo {
    approverName: string;
    status: ApprovalStatus;
}

export interface IApproverColumn {
    approvers: IApproverInfo[];
}

/**
 * Typed shape of a single Bills grid row, keyed by column jsonKey. The grid is typed
 * `TData = IBillGridRow`, so cell renderers read `params.data.<jsonKey>` with a real type instead
 * of casting `params.value`. Only rendered columns are typed; other server fields are allowed via
 * the index signature (the payload carries more than the grid shows).
 */
export interface IBillGridRow extends IBaseEntity {
    // Bill-specific columns
    billNumberLink: IBillLinkResponse | null;
    billTitleLink: IBillLinkResponse | null;
    payTo: IBillPayToResponse | null;
    paymentAmount: IBillAmountCellValue | null;
    amountPaid: IBillAmountFooterValue | null;
    remainingBalance: IBillAmountFooterValue | null;
    dueDate: IDueDateResponse | null;
    purchaseOrderPaymentStatus: IBillStatusResponse | null;
    approvers: IApproverColumn | null;
    documentType: string | null;
    purchaseOrderPaymentAccountingStatus: BilledStatus | null;
    linkedScheduleItemStatus: LinkedScheduleItemStatus | null;
    lienWaiverStatus: ILienWaiverStatusResponse | null;
    relatedPurchaseOrders: IEntityLinkResponse[] | null;
    relatedOwnerInvoices: IEntityLinkResponse[] | null;

    // Generic columns rendered by standardColumnImplementations (raw values)
    jobName: string | null;
    paidBy: string | null;
    billSource: string | null;
    invoicedDate: string | null;
    datePaid: string | null;
    createdDate: string | null;
    costCodes: string[] | null;
    varianceCodes: string[] | null;

    // The server payload includes more fields than the grid renders.
    [key: string]: unknown;
}
